import { useState } from "react";
import { formatText, useTexts, type Locale } from "../texts";
import { ChatIcon } from "./icons/Icon";
import styles from "./RangeCalendar.module.css";

// C07/C34 · «Elegir fechas» del Admin (R7, AR17): el selector de rango del catálogo, como el de Estadísticas del portal.
// Escritorio: el mes anterior y el actual lado a lado con ‹ y ›; celular (`months={1}`): un mes. Sin rango aplicado abre
// sin días elegidos (solo el anillo de hoy); el segundo clic antes del primero invierte el orden; los días futuros no se
// eligen; más de 366 días deja «Aplicar» desactivado con el motivo. Fechas como «aaaa-mm-dd» (días de Lima).

type Day = string;

const pad = (value: number) => String(value).padStart(2, "0");
const iso = (year: number, month: number, day: number): Day => `${year}-${pad(month + 1)}-${pad(day)}`;
const parts = (day: Day) => day.split("-").map(Number) as [number, number, number];
const days = (from: Day, to: Day) => Math.round((Date.parse(to) - Date.parse(from)) / 86400000) + 1;

/** Hoy en Lima (UTC−5, sin horario de verano), «aaaa-mm-dd». */
export function limaToday(now = new Date()): Day {
  return new Date(now.getTime() - 5 * 3600 * 1000).toISOString().slice(0, 10);
}

/** «17 set. – 16 oct.» con el año si los dos años difieren (C34: la píldora con un rango muestra el rango). */
export function rangeText(locale: Locale, from: Day, to: Day): string {
  const [fy] = parts(from);
  const [ty] = parts(to);
  const short = (day: Day) => {
    const [y, m, d] = parts(day);
    const text = new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "short",
      ...(fy !== ty ? { year: "numeric" } : {}),
      timeZone: "UTC",
    }).format(new Date(Date.UTC(y, m - 1, d)));
    return locale === "es-PE" ? text.replace("sept.", "set.").replace("sep.", "set.") : text;
  };
  return `${short(from)} – ${short(to)}`;
}

/** «Setiembre de 2026»: solo la primera letra en mayúscula (C24, es-PE con «setiembre»). */
const monthTitle = (locale: Locale, title: string) => {
  const text = locale === "es-PE" ? title.replace("septiembre", "setiembre") : title;
  return text.charAt(0).toUpperCase() + text.slice(1);
};

export function RangeCalendar({
  from,
  to,
  months = 2,
  onApply,
  onCancel,
}: {
  /** Rango aplicado (o null: abre sin días elegidos). */
  from: Day | null;
  to: Day | null;
  months?: 1 | 2;
  onApply: (range: { from: Day; to: Day }) => void;
  onCancel: () => void;
}) {
  const { L, locale } = useTexts();
  const today = limaToday();
  const [start, setStart] = useState<Day | null>(from);
  const [end, setEnd] = useState<Day | null>(to);
  // El último mes a la vista: el del fin del rango aplicado o el actual; con dos meses, el anterior a su izquierda.
  const [anchor, setAnchor] = useState(() => {
    const [y, m] = parts(to ?? today);
    return { year: y, month: m - 1 };
  });
  const [todayYear, todayMonth] = parts(today);
  const canNext = anchor.year < todayYear || (anchor.year === todayYear && anchor.month < todayMonth - 1);
  const shift = (step: number) =>
    setAnchor((current) => {
      const date = new Date(Date.UTC(current.year, current.month + step, 1));
      return { year: date.getUTCFullYear(), month: date.getUTCMonth() };
    });
  const select = (day: Day) => {
    if (!start || end) {
      setStart(day);
      setEnd(null);
      return;
    }
    // El segundo clic antes del primero invierte el orden: un rango al revés es imposible.
    if (day < start) {
      setEnd(start);
      setStart(day);
    } else setEnd(day);
  };
  const count = start && end ? days(start, end) : start ? 1 : 0;
  const tooLong = count > 366;
  const visible = Array.from({ length: months }, (_, index) => {
    const date = new Date(Date.UTC(anchor.year, anchor.month - (months - 1 - index), 1));
    return { year: date.getUTCFullYear(), month: date.getUTCMonth() };
  });
  const weekdays = Array.from({ length: 7 }, (_, index) =>
    new Intl.DateTimeFormat(locale, { weekday: "narrow", timeZone: "UTC" }).format(
      new Date(Date.UTC(2024, 0, 1 + index)),
    ),
  );
  return (
    <div className={styles.calendar} data-part="calendario">
      <div className={styles.months} style={{ ["--months" as string]: String(months) }}>
        {visible.map((view, index) => {
          const first = new Date(Date.UTC(view.year, view.month, 1));
          const blanks = (first.getUTCDay() + 6) % 7;
          const total = new Date(Date.UTC(view.year, view.month + 1, 0)).getUTCDate();
          const title = new Intl.DateTimeFormat(locale, { month: "long", year: "numeric", timeZone: "UTC" }).format(
            first,
          );
          return (
            <div key={`${view.year}-${view.month}`} className={styles.month}>
              <div className={styles.monthHead}>
                {index === 0 ? (
                  <button
                    type="button"
                    className={styles.arrow}
                    aria-label={L("ADM_CAL_PREVIOUS")}
                    title={L("ADM_CAL_PREVIOUS")}
                    onClick={() => shift(-1)}
                  >
                    <ChatIcon name="chevron-left" />
                  </button>
                ) : (
                  <span className={styles.arrowSpace} />
                )}
                <strong>{monthTitle(locale, title)}</strong>
                {index === visible.length - 1 ? (
                  <button
                    type="button"
                    className={styles.arrow}
                    aria-label={L("ADM_CAL_NEXT")}
                    title={L("ADM_CAL_NEXT")}
                    disabled={!canNext}
                    onClick={() => shift(1)}
                  >
                    <ChatIcon name="chevron-right" />
                  </button>
                ) : (
                  <span className={styles.arrowSpace} />
                )}
              </div>
              <div className={styles.grid} role="grid" aria-label={title}>
                {weekdays.map((name, position) => (
                  <span key={position} className={styles.weekday} aria-hidden="true">
                    {name}
                  </span>
                ))}
                {Array.from({ length: blanks }, (_, position) => (
                  <span key={`b${position}`} />
                ))}
                {Array.from({ length: total }, (_, position) => {
                  const day = iso(view.year, view.month, position + 1);
                  const future = day > today;
                  const edge = day === start || day === end;
                  const inside = !!start && !!end && day > start && day < end;
                  return (
                    <button
                      key={day}
                      type="button"
                      className={[
                        styles.day,
                        edge ? styles.edge : "",
                        inside ? styles.inside : "",
                        day === start && end && end !== start ? styles.startEdge : "",
                        day === end && start !== end ? styles.endEdge : "",
                        day === today ? styles.today : "",
                      ].join(" ")}
                      disabled={future}
                      aria-pressed={edge || inside ? "true" : "false"}
                      aria-label={day}
                      onClick={() => select(day)}
                    >
                      {position + 1}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      {tooLong ? (
        <p className={styles.error} role="alert">
          {L("ADM_CAL_TOO_LONG")}
        </p>
      ) : null}
      <div className={styles.foot}>
        <span className={styles.summary}>
          {start
            ? `${rangeText(locale, start, end ?? start)} · ${formatText(L(count === 1 ? "ADM_CAL_DAY_ONE" : "ADM_CAL_DAYS"), count)}`
            : L("ADM_CAL_PICK")}
        </span>
        <button type="button" className="tm-btn secondary" onClick={onCancel}>
          {L("ACTION_CANCEL")}
        </button>
        <button
          type="button"
          className="tm-btn primary"
          disabled={!start || tooLong}
          onClick={() => start && onApply({ from: start, to: end ?? start })}
        >
          {L("ADM_AI_APPLY")}
        </button>
      </div>
    </div>
  );
}
